import {autoUpdate, computePosition, flip, offset, Placement, shift} from "@floating-ui/dom";

import {detectElementRemoved} from "../../utils/detectElementRemoved";

// This file is not named as *.client.ts because it should not be built by webpack.
// This file is meant to be included in the global script for each client.

export type TooltipArgs = {
  id: string;
  placement: Placement;
};

const addBodyEventListener = (eventName: string, listener: EventListenerOrEventListenerObject) => {
  document.body.addEventListener(eventName, listener);
};

/**
 * Tooltips are shown by hovering over a trigger element.
 *
 * Attributes:
 * - data-tooltip-content - Marks the element containing the tooltip content.
 *   Must be a direct child of the tooltip element.
 *
 * - data-tooltip-trigger - Marks the element that triggers the tooltip on hover.
 *   Must be a direct child of the tooltip element.
 */
export const initTooltip = (tooltipEl: Element, force: boolean) => {
  const data = tooltipEl.getAttribute("data-tooltip");
  if (!data) {
    console.error("No data found for tooltip", tooltipEl);
    return;
  }

  const tooltipData = JSON.parse(data) as TooltipArgs;

  const initialized = tooltipEl.getAttribute("data-tooltip-initialized");
  if (initialized && !force) {
    return;
  }
  tooltipEl.setAttribute("data-tooltip-initialized", "true");

  const contentElement = tooltipEl.querySelector<HTMLElement>("[data-tooltip-content]");
  const triggerElement = tooltipEl.querySelector<HTMLElement>("[data-tooltip-trigger]");
  if (!contentElement || !triggerElement) {
    return;
  }

  const content: HTMLElement = contentElement;
  const trigger: HTMLElement = triggerElement;
  const placement = tooltipData.placement || "top";

  // Store original parent for restoration
  const originalParent = content.parentElement;
  if (!originalParent) {
    return;
  }

  let clean: (() => void) | null = null;
  const hideTooltipOnHistorySave = () => {
    hideTooltip();
  };

  function update() {
    computePosition(trigger, content, {
      placement,
      middleware: [offset(6), flip(), shift({padding: 5})],
    }).then(({x, y}) => {
      Object.assign(content.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  }

  function showTooltip() {
    // Move to body and set styles
    document.body.appendChild(content);
    Object.assign(content.style, {
      zIndex: "9999",
      display: "block",
    });

    update();
    clean = autoUpdate(trigger, content, update);

    addBodyEventListener("htmx:beforeHistorySave", hideTooltipOnHistorySave);
  }

  function hideTooltip() {
    // Move back to original parent
    // oxlint-disable-next-line typescript-eslint/no-non-null-assertion
    originalParent!.appendChild(content);
    Object.assign(content.style, {
      zIndex: "",
      display: "",
    });

    clean?.();
    clean = null;
    document.body.removeEventListener("htmx:beforeHistorySave", hideTooltipOnHistorySave);
  }

  trigger.addEventListener("mouseenter", showTooltip);
  trigger.addEventListener("mouseleave", hideTooltip);
  trigger.addEventListener("focus", showTooltip);
  trigger.addEventListener("blur", hideTooltip);

  detectElementRemoved(trigger, () => {
    hideTooltip();
    trigger.removeEventListener("mouseenter", showTooltip);
    trigger.removeEventListener("mouseleave", hideTooltip);
    trigger.removeEventListener("focus", showTooltip);
    trigger.removeEventListener("blur", hideTooltip);
  });
};

// oxlint-disable-next-line typescript-eslint/no-explicit-any
export const addTooltipScript = (obj: any) => {
  obj.initTooltip = initTooltip;
};

addBodyEventListener("htmx:load", () => {
  const tooltips = document.querySelectorAll<HTMLElement>("[data-tooltip]");
  tooltips.forEach((tooltip) => {
    initTooltip(tooltip, false);
  });
});

addBodyEventListener("htmx:historyRestore", () => {
  const tooltips = document.querySelectorAll<HTMLElement>("[data-tooltip]");
  tooltips.forEach((tooltip) => {
    initTooltip(tooltip, true);
  });
});
